import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring, AnimatePresence } from "framer-motion";
import { Sparkles, Play, X } from "lucide-react";
import { AppStoreButton } from "./AppStoreButton";
import { MockPhoneFrame, MockHome, MockGroup, MockFollowups, MockActions } from "./MockPhone";

const line = {
  initial: { y: "115%", opacity: 0 },
  animate: (i) => ({
    y: 0, opacity: 1,
    transition: { duration: 0.95, ease: [0.16, 1, 0.3, 1], delay: 0.2 + i * 0.09 },
  }),
};

const scrollTo = (id) => {
  const el = document.querySelector(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Hero() {
  const ref = useRef(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const yPhone = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 22 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 22 });

  useEffect(() => {
    const on = (e) => {
      mx.set(e.clientX / window.innerWidth - 0.5);
      my.set(e.clientY / window.innerHeight - 0.5);
    };
    window.addEventListener("mousemove", on);
    return () => window.removeEventListener("mousemove", on);
  }, [mx, my]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative min-h-[115vh] pt-32 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Unified gradient wash — evenly distributed across the whole hero */}
      <motion.div
        style={{ y: yBg }}
        className="absolute inset-0 pointer-events-none"
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(45% 40% at 15% 22%, rgba(232,90,42,0.22), transparent 70%), radial-gradient(45% 40% at 85% 22%, rgba(30,58,196,0.22), transparent 70%), radial-gradient(60% 55% at 50% 55%, rgba(124,58,237,0.22), transparent 70%), radial-gradient(50% 40% at 20% 85%, rgba(232,90,42,0.16), transparent 70%), radial-gradient(50% 40% at 80% 85%, rgba(30,58,196,0.16), transparent 70%)",
          }}
        />
      </motion.div>
      {/* Soft top glow to blur the seam at the header */}
      <div
        className="absolute inset-x-0 top-0 h-40 pointer-events-none"
        style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.6), transparent)" }}
      />
      {/* Soft bottom fade into the next section */}
      <div
        className="absolute inset-x-0 bottom-0 h-40 pointer-events-none"
        style={{ background: "linear-gradient(0deg, var(--bg), transparent)" }}
      />
      <div
        className="absolute inset-0 opacity-[0.35] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(10,10,31,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(10,10,31,0.04) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
          maskImage: "radial-gradient(ellipse 60% 60% at 50% 50%, black 40%, transparent 100%)",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05, duration: 0.6 }}
              className="chip"
              data-testid="hero-chip"
            >
              <Sparkles className="w-3.5 h-3.5 text-[color:var(--accent)]" />
              <span>Coming soon · iOS + Android</span>
            </motion.div>

            <h1
              className="font-display font-medium text-[color:var(--ink)] mt-6 leading-[0.9] text-[13vw] md:text-[9vw] lg:text-[7.2vw]"
              data-testid="hero-headline"
            >
              <span className="mask-line">
                <motion.span variants={line} custom={0} initial="initial" animate="animate" className="block">
                  Organize.
                </motion.span>
              </span>
              <span className="mask-line">
                <motion.span
                  variants={line} custom={1} initial="initial" animate="animate"
                  className="block font-serif-italic pl-6 md:pl-16 gradient-text"
                  style={{ fontStyle: "italic", fontFamily: "var(--font-serif)", letterSpacing: "-0.02em" }}
                >
                  Automate.
                </motion.span>
              </span>
              <span className="mask-line">
                <motion.span
                  variants={line} custom={2} initial="initial" animate="animate"
                  className="block" style={{ color: "var(--accent)" }}
                >
                  Grow.
                </motion.span>
              </span>
            </h1>

            <span className="mask-line mt-8 block max-w-xl">
              <motion.p
                variants={line} custom={3} initial="initial" animate="animate"
                className="text-[color:var(--ink-soft)] text-lg md:text-xl leading-relaxed"
                data-testid="hero-subhead"
              >
                Your phonebook holds thousands of names. BizBinder turns them into a business
                network you can actually use — organized into groups, tagged with notes, and
                followed up on time. Every time.
              </motion.p>
            </span>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.95, duration: 0.7 }}
              className="mt-10 flex flex-wrap items-center gap-3"
            >
              <AppStoreButton variant="apple" testid="hero-cta-app-store" onClick={() => scrollTo("#waitlist")} />
              <AppStoreButton variant="google" testid="hero-cta-play-store" onClick={() => scrollTo("#waitlist")} />
              <button
                data-testid="hero-watch-demo-btn"
                onClick={() => setVideoOpen(true)}
                className="group ml-1 rounded-full h-14 pl-5 pr-3 border border-[color:var(--line-strong)] hover:border-[color:var(--ink)] text-[color:var(--ink)] flex items-center gap-3 font-medium transition-colors bg-white/60 backdrop-blur"
              >
                <span>Watch demo</span>
                <span className="w-9 h-9 rounded-full bg-[color:var(--blue)] flex items-center justify-center">
                  <Play className="w-3.5 h-3.5 text-white ml-0.5 fill-white" />
                </span>
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3, duration: 0.8 }}
              className="mt-10 flex items-center gap-6 text-[color:var(--ink-mute)] text-xs"
              data-testid="hero-social-proof"
            >
              <span className="overline">Your network · Your data · No ads</span>
              <div className="flex-1 h-px bg-[color:var(--line-strong)] max-w-24" />
            </motion.div>
          </div>

          <motion.div
            style={{ y: yPhone, rotateX: rx, rotateY: ry, transformPerspective: 1400 }}
            className="lg:col-span-5 relative"
            data-testid="hero-device"
          >
            <PhoneStack />
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-6 md:left-10 flex items-center gap-3 text-[color:var(--ink-mute)] text-xs"
      >
        <div className="w-6 h-10 border border-[color:var(--line-strong)] rounded-full flex items-start justify-center py-2">
          <motion.div
            className="w-1 h-2 bg-[color:var(--ink)] rounded-full"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
        <span className="uppercase tracking-[0.24em]">Scroll</span>
      </motion.div>

      <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} />
    </section>
  );
}

function VideoModal({ open, onClose }) {
  const scenes = [
    { Screen: MockHome,      caption: "Your home — every group in one glance." },
    { Screen: MockGroup,     caption: "Sub-categories keep 500 contacts feeling like 50." },
    { Screen: MockActions,   caption: "Scan a card, add a contact, or set a reminder in one tap." },
    { Screen: MockFollowups, caption: "Today, Upcoming, Overdue — nothing slips." },
  ];
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!open) return;
    setIdx(0);
    const t = setInterval(() => setIdx((v) => (v + 1) % scenes.length), 2800);
    return () => clearInterval(t);
  }, [open, scenes.length]);

  const Current = scenes[idx].Screen;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[80] flex items-center justify-center p-6"
          data-testid="video-modal"
        >
          <div
            className="absolute inset-0 bg-[#05050a]/70 backdrop-blur-md"
            onClick={onClose}
          />
          <motion.div
            initial={{ scale: 0.92, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.94, opacity: 0, y: 10 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-3xl bg-white rounded-3xl border border-black/10 overflow-hidden shadow-2xl"
          >
            <div className="flex items-center justify-between p-5 border-b border-black/10">
              <div className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-[color:var(--accent)] animate-pulse" />
                <div className="text-sm text-[color:var(--ink)] font-medium">BizBinder — Product walkthrough</div>
              </div>
              <button
                onClick={onClose}
                data-testid="video-modal-close"
                className="w-9 h-9 rounded-full hover:bg-black/5 flex items-center justify-center"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-6 p-8 items-center bg-gradient-to-br from-[color:var(--purple-soft)] via-white to-[color:var(--accent-soft)]">
              <div className="relative mx-auto w-[220px] md:w-[240px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.02 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <MockPhoneFrame>
                      <Current />
                    </MockPhoneFrame>
                  </motion.div>
                </AnimatePresence>
              </div>
              <div>
                <div className="overline mb-3">Scene {idx + 1} of {scenes.length}</div>
                <AnimatePresence mode="wait">
                  <motion.h4
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.4 }}
                    className="font-display text-2xl md:text-3xl text-[color:var(--ink)] leading-tight tracking-tight"
                  >
                    {scenes[idx].caption}
                  </motion.h4>
                </AnimatePresence>
                <div className="mt-6 flex gap-1.5">
                  {scenes.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setIdx(i)}
                      className={`h-1 rounded-full transition-all ${
                        i === idx ? "w-8 bg-[color:var(--ink)]" : "w-4 bg-black/15"
                      }`}
                      aria-label={`Scene ${i + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* Time-of-day greeting helper */
function useGreeting() {
  const h = new Date().getHours();
  if (h < 5)  return "Good night,";
  if (h < 12) return "Good morning,";
  if (h < 17) return "Good afternoon,";
  if (h < 21) return "Good evening,";
  return "Good night,";
}

/* Auto-cycling front phone screen */
function CyclingPhone({ greeting }) {
  const screens = [
    { key: "home",      render: () => <MockHome greeting={greeting} name="Marcus" /> },
    { key: "group",     render: () => <MockGroup /> },
    { key: "followups", render: () => <MockFollowups /> },
    { key: "actions",   render: () => <MockActions /> },
  ];
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % screens.length), 4200);
    return () => clearInterval(t);
  }, [screens.length]);
  return (
    <MockPhoneFrame>
      <AnimatePresence mode="wait">
        <motion.div
          key={screens[i].key}
          initial={{ opacity: 0, y: 12, scale: 0.985 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.99 }}
          transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-0 flex flex-col"
        >
          {screens[i].render()}
        </motion.div>
      </AnimatePresence>
    </MockPhoneFrame>
  );
}

/* Live activity chip stream */
const activityPool = [
  { kicker: "Contact added",   text: "Sofia Rossi · Suppliers",     icon: "user",   tint: "blue"    },
  { kicker: "Card scanned",    text: "Hannah Weiss · 2.1s",         icon: "scan",   tint: "accent"  },
  { kicker: "Reminder set",    text: "Alex Bennett · Tmrw 10 AM",   icon: "bell",   tint: "accent"  },
  { kicker: "Voice note",      text: "&ldquo;Follow up Saturday…&rdquo;", icon: "mic",  tint: "blue" },
  { kicker: "Group updated",   text: "Distributors +3",             icon: "users",  tint: "purple"  },
  { kicker: "Follow-up done",  text: "Priya Kapoor · Closed",       icon: "check",  tint: "emerald" },
  { kicker: "Duplicate found", text: "Merged 2 contacts",           icon: "layers", tint: "purple"  },
  { kicker: "WhatsApp sent",   text: "Quote v2 · Alex",             icon: "wa",     tint: "emerald" },
];

const tints = {
  blue:    { bg: "var(--blue-soft)",   fg: "var(--blue)"   },
  accent:  { bg: "var(--accent-soft)", fg: "var(--accent)" },
  purple:  { bg: "var(--purple-soft)", fg: "var(--purple)" },
  emerald: { bg: "#D1FAE5",            fg: "#059669"       },
};

function ChipIcon({ name, color }) {
  const s = 14;
  const common = { width: s, height: s, fill: "none", stroke: color, strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };
  switch (name) {
    case "user":   return <svg {...common} viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
    case "users":  return <svg {...common} viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
    case "scan":   return <svg {...common} viewBox="0 0 24 24"><path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/><path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/><line x1="7" y1="12" x2="17" y2="12"/></svg>;
    case "bell":   return <svg {...common} viewBox="0 0 24 24"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>;
    case "mic":    return <svg {...common} viewBox="0 0 24 24"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/></svg>;
    case "check":  return <svg {...common} viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>;
    case "layers": return <svg {...common} viewBox="0 0 24 24"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>;
    case "wa":     return <svg {...common} viewBox="0 0 24 24"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>;
    default:       return null;
  }
}

function ActivityChip({ index, poolOffset, positionClass, fromX }) {
  const [n, setN] = useState((index + poolOffset) % activityPool.length);
  useEffect(() => {
    const t = setInterval(() => {
      setN((v) => (v + 4) % activityPool.length);
    }, 4600);
    return () => clearInterval(t);
  }, []);
  const it = activityPool[n];
  const tint = tints[it.tint];

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={n}
        initial={{ opacity: 0, x: fromX, scale: 0.94 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        exit={{ opacity: 0, x: fromX, scale: 0.94 }}
        transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
        className={`absolute ${positionClass} rounded-2xl border border-[color:var(--line)] bg-white/95 backdrop-blur-xl px-4 py-3 text-xs text-[color:var(--ink)] shadow-2xl z-40`}
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: tint.bg }}>
            <ChipIcon name={it.icon} color={tint.fg} />
          </div>
          <div>
            <div className="text-[10px] text-[color:var(--ink-mute)] uppercase tracking-widest">
              {it.kicker}
            </div>
            <div
              className="font-medium leading-tight"
              dangerouslySetInnerHTML={{ __html: it.text }}
            />
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function PhoneStack() {
  const greeting = useGreeting();
  return (
    <div className="relative mx-auto w-[300px] md:w-[340px]" style={{ perspective: 1400 }}>
      {/* Front phone — AUTO-CYCLING screens */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{
          opacity: 1,
          y: [0, -8, 0],
        }}
        transition={{
          opacity: { duration: 1.1, delay: 0.95, ease: [0.16, 1, 0.3, 1] },
          y: { duration: 5, delay: 1.95, repeat: Infinity, ease: "easeInOut" },
        }}
        className="relative z-30"
      >
        <div
          className="absolute -inset-20 rounded-full opacity-60 blur-3xl pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 50% 40%, rgba(232,90,42,0.28), rgba(30,58,196,0.24) 40%, transparent 70%)",
          }}
        />
        <CyclingPhone greeting={greeting} />
      </motion.div>

      {/* Live activity chip stream — 4 chips cycle through activityPool */}
      <ActivityChip index={0} poolOffset={0} positionClass="-left-14 md:-left-28 top-16"     fromX={-90} />
      <ActivityChip index={1} poolOffset={1} positionClass="-right-10 md:-right-24 top-2"    fromX={90}  />
      <ActivityChip index={2} poolOffset={2} positionClass="-left-16 md:-left-28 bottom-28"  fromX={-90} />
      <ActivityChip index={3} poolOffset={3} positionClass="-right-12 md:-right-24 bottom-4" fromX={90}  />
    </div>
  );
}
